import { useEffect, useMemo, useState } from "react";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import VideoModal from "@/components/feature/VideoModal";
import type { VideoItem } from "@/types/content";
import { videos } from "@/mocks/videos";
import { streamers } from "@/mocks/streamers";

const allVideos = videos as VideoItem[];
const VIDEO_TYPES = ["全部", "采访", "节目片段", "写真MV"];
const PAGE_SIZE = 6;

const streamerOptions = ["全部主播", ...streamers.map((s) => s.name)];

export default function Videos() {
  const [streamerName, setStreamerName] = useState("全部主播");
  const [type, setType] = useState("全部");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [playing, setPlaying] = useState<VideoItem | null>(null);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [streamerName, type]);

  const filtered = useMemo(() => {
    return allVideos
      .filter((v) => (streamerName === "全部主播" ? true : v.streamerName === streamerName))
      .filter((v) => (type === "全部" ? true : v.type === type))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [streamerName, type]);

  const visible = filtered.slice(0, visibleCount);

  const handleReset = () => {
    setStreamerName("全部主播");
    setType("全部");
  };

  return (
    <>
      <Seo
        title="韩国女主播视频专辑 – 韩国女主播"
        description="聚合采访、节目片段与写真 MV 等韩国女主播视频内容，支持按主播名称与视频类型筛选，点击即可在线预览主播相关视频精选。"
        keywords="韩国女主播视频,主播采访视频,节目片段,写真MV,韩国主播视频合集"
        canonical="/videos"
      />

      <div className="mx-auto max-w-[1440px] px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: "视频专辑" }]} />

        <header className="mt-4 mb-6">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">视频专辑</h1>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-3xl">
            汇集韩国女主播的采访实录、节目片段与写真 MV。可按主播或视频类型筛选，点击视频卡片即可在线预览播放。
          </p>
        </header>

        <div className="rounded-lg bg-background-50 border border-background-200/80 p-4 md:p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-xs font-medium text-foreground-500 sm:w-20 shrink-0">主播名称</span>
            <div className="relative sm:w-64">
              <i className="ri-user-voice-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <select
                value={streamerName}
                onChange={(e) => setStreamerName(e.target.value)}
                className="w-full h-10 pl-9 pr-8 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-accent-400 transition-colors cursor-pointer appearance-none"
              >
                {streamerOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-xs font-medium text-foreground-500 sm:w-20 shrink-0">视频类型</span>
            <div className="flex flex-wrap gap-2">
              {VIDEO_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    type === t ? "bg-accent-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-background-200/70">
            <p className="text-xs text-foreground-500">
              共 <span className="font-semibold text-accent-600">{filtered.length}</span> 个视频
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-1 text-xs text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-video-off-line text-3xl"></i>
            </span>
            <h3 className="font-heading font-semibold text-foreground-900 mb-2">没有匹配的视频</h3>
            <p className="text-sm text-foreground-500 mb-5">换个主播或视频类型试试</p>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-6">
              {visible.map((video) => (
                <VideoCard key={video.id} video={video} onPlay={setPlaying} />
              ))}
            </div>

            {visibleCount < filtered.length && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  加载更多（剩余 {filtered.length - visibleCount} 个）
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <VideoModal video={playing} onClose={() => setPlaying(null)} />
    </>
  );
}